import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { 
  Bell, 
  Package, 
  CreditCard, 
  ShoppingCart, 
  AlertCircle, 
  CheckCircle, 
  Info,
  Trash2,
  Check,
  X,
  Banknote,
  HeadphonesIcon
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Button } from '@/components/ui/button';
import { Separator } from '@/components/ui/separator';
import { cn } from '@/lib/utils';
import { useNotifications } from '@/hooks/useNotifications';

interface NotificationItem {
  id: string;
  title: string;
  message: string;
  type: string;
  severity: string;
  read: boolean;
  created_at: string;
  metadata?: any;
  link?: string;
}

export function NotificationCenter() {
  const navigate = useNavigate();
  const { notifications, loading, markAsRead, markAllAsRead, deleteNotification } = useNotifications();
  
  const getNotificationLink = (notification: NotificationItem) => {
    // Enhanced navigation based on notification type and metadata
    if (notification.link) return notification.link;
    
    switch (notification.type) {
      case 'order':
        if (notification.metadata?.order_id) {
          return `/seller/orders`;
        }
        return '/seller/dashboard';
      case 'escrow':
        return '/seller/dashboard';
      case 'support':
        return '/seller/dashboard?tab=support';
      case 'admin':
        return '/admin/dashboard';
      default:
        return '/seller/dashboard';
    }
  };

  const getIcon = (type: string, severity: string) => {
    const iconClass = cn(
      'h-4 w-4',
      severity === 'success' && 'text-green-500',
      severity === 'error' && 'text-red-500',
      severity === 'warning' && 'text-yellow-500',
      severity === 'info' && 'text-blue-500'
    );

    switch (type) {
      case 'order':
        return <ShoppingCart className={iconClass} />;
      case 'payment':
        return <CreditCard className={iconClass} />;
      case 'product':
        return <Package className={iconClass} />;
      case 'escrow':
        return <Banknote className={iconClass} />;
      case 'support':
        return <HeadphonesIcon className={iconClass} />;
      case 'system':
      default:
        switch (severity) {
          case 'success':
            return <CheckCircle className={iconClass} />;
          case 'error':
            return <AlertCircle className={iconClass} />;
          case 'warning':
            return <AlertCircle className={iconClass} />;
          default:
            return <Info className={iconClass} />;
        }
    }
  };

  const handleNotificationClick = async (notification: NotificationItem) => {
    // Mark as read
    if (!notification.read) {
      await markAsRead([notification.id]);
    }

    // Navigate to appropriate page
    const link = getNotificationLink(notification);
    if (link) {
      navigate(link);
    }
  };

  if (loading) {
    return (
      <div className="p-4 text-center text-muted-foreground">
        Loading notifications...
      </div>
    );
  }

  return (
    <div className="w-full">
      <div className="flex items-center justify-between p-4 border-b">
        <div className="flex items-center gap-2">
          <Bell className="h-4 w-4" />
          <h4 className="font-semibold">Notifications</h4>
        </div>
        {notifications.length > 0 && (
          <div className="flex gap-1">
            <Button
              variant="ghost"
              size="sm"
              onClick={markAllAsRead}
              title="Mark all as read"
            >
              <Check className="h-4 w-4" />
            </Button>
          </div>
        )}
      </div>

      {notifications.length === 0 ? (
        <div className="p-8 text-center text-muted-foreground">
          <Bell className="h-12 w-12 mx-auto mb-3 opacity-50" />
          <p>No notifications yet</p>
          <p className="text-sm mt-1">You'll see updates here when they arrive</p>
        </div>
      ) : (
        <ScrollArea className="h-[400px]">
          <div className="divide-y">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={cn(
                  'p-4 hover:bg-accent/80 cursor-pointer transition-all relative group border-l-4',
                  !notification.read && 'bg-accent/30 border-primary shadow-sm',
                  notification.read && 'border-transparent hover:border-muted-foreground/20',
                  notification.severity === 'success' && !notification.read && 'border-green-500 bg-green-50/50 dark:bg-green-950/20',
                  notification.severity === 'error' && !notification.read && 'border-red-500 bg-red-50/50 dark:bg-red-950/20',
                  notification.severity === 'warning' && !notification.read && 'border-yellow-500 bg-yellow-50/50 dark:bg-yellow-950/20'
                )}
                onClick={() => handleNotificationClick(notification)}
              >
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-2 right-2 h-6 w-6 opacity-0 group-hover:opacity-100 transition-opacity"
                  onClick={(e) => {
                    e.stopPropagation();
                    deleteNotification(notification.id);
                  }}
                >
                  <X className="h-3 w-3" />
                </Button>

                <div className="flex gap-3">
                  <div className="flex-shrink-0 mt-1">
                    <div className={cn(
                      'p-2 rounded-full',
                      notification.severity === 'success' && 'bg-green-100 dark:bg-green-900/30',
                      notification.severity === 'error' && 'bg-red-100 dark:bg-red-900/30',
                      notification.severity === 'warning' && 'bg-yellow-100 dark:bg-yellow-900/30',
                      notification.severity === 'info' && 'bg-blue-100 dark:bg-blue-900/30'
                    )}>
                      {getIcon(notification.type, notification.severity)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <p className={cn(
                        'font-medium text-sm leading-tight',
                        !notification.read && 'text-foreground',
                        notification.read && 'text-muted-foreground'
                      )}>
                        {notification.title}
                      </p>
                      {!notification.read && (
                        <div className="flex-shrink-0 ml-2">
                          <div className="h-2 w-2 bg-primary rounded-full animate-pulse" />
                        </div>
                      )}
                    </div>
                    <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                      {notification.message}
                    </p>
                    <div className="flex items-center justify-between mt-2">
                      <p className="text-xs text-muted-foreground">
                        {format(new Date(notification.created_at), 'MMM d, h:mm a')}
                      </p>
                      {notification.metadata?.amount && (
                        <span className="text-xs font-medium text-green-600 dark:text-green-400">
                          ₦{Number(notification.metadata.amount).toLocaleString()}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}